import { useState } from 'react';
import SEOHelmet from '@/components/base/SEOHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { reviews } from '@/mocks/faqs';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9p1poiftmtnfcfh0g20';

const AboutPage = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送！我们会尽快回复您。');
      form.reset();
      return;
    }

    setFormStatus('submitting');

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送！我们会尽快回复您。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫屋 - 韩国漫画推荐平台',
    description: '韩漫屋致力于为中文读者提供高质量的韩国漫画推荐服务，资源新颖画质优良，是值得信赖的漫画收藏平台。',
    url: 'https://hanmanwu.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '韩漫屋',
      description: '韩漫屋提供韩国漫画和高质量漫画推荐韩漫在线免费阅读，资源新颖画质优良，是您漫画收藏的可靠平台。',
      url: 'https://hanmanwu.com/',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@hanmanwu.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  return (
    <>
      <SEOHelmet
        title="关于我们_韩国漫画推荐平台_韩漫屋"
        description="韩漫屋致力于为中文读者提供高质量的韩国漫画推荐服务，资源新颖画质优良。了解我们的品牌故事、用户口碑和联系方式。"
        keywords="韩漫屋,关于韩漫屋,韩国漫画推荐平台,韩漫推荐"
        canonicalUrl="https://hanmanwu.com/about"
        schema={schema}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            <div className="py-10 md:py-16">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading text-center">
                关于 <span className="text-primary-500">韩漫</span>屋
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed">
                为中文读者打造的韩国漫画推荐平台，让每一部好作品都能遇见懂它的读者
              </p>
            </div>

            <section className="pb-14 md:pb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-5">
                    我们的故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      韩漫屋诞生于一群韩国漫画爱好者的共同热情。我们发现，尽管韩国漫画在全球范围内越来越受欢迎，
                      但中文读者往往难以找到高质量、有深度的漫画推荐。网上的信息杂乱无章，评分体系混乱，
                      真正优秀的作品常常被埋没在海量内容之中。
                    </p>
                    <p>
                      于是，我们在2024年创立了韩漫屋——一个专注于<strong>韩国漫画推荐</strong>的中文平台。
                      我们的编辑团队由资深的漫画爱好者和专业评论人组成，每一部推荐的作品都经过精心筛选和多维度评测，
                      确保为读者提供最真实、最有价值的参考。
                    </p>
                    <p>
                      两年来，韩漫屋已经成长为拥有超过50万月活读者的漫画推荐平台，
                      累计推荐了200余部高质量的<strong>韩国漫画</strong>作品。
                      我们始终坚持资源新颖、画质优良的标准，让每一位来到韩漫屋的读者都能找到自己心仪的作品。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20Korean%20manhwa%20reading%20scene%20with%20multiple%20people%20reading%20comics%20in%20a%20bright%20modern%20library%20space%2C%20natural%20light%20streaming%20through%20large%20windows%2C%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20Korean%20aesthetic%2C%20warm%20wooden%20tones%2C%20peaceful%20communal%20atmosphere%2C%20clean%20composition&width=800&height=600&seq=about-hanmanwu&orientation=landscape"
                    alt="韩漫屋团队 - 韩国漫画推荐平台"
                    title="韩漫屋 - 韩国漫画推荐"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>

            <section className="py-14 md:py-20 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-10">
                  读者口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {reviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm text-primary-400"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        "{review.content}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary-600">
                            {review.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-14 md:py-20">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 text-center mb-8">
                  有任何建议、合作意向或问题，欢迎随时联系我们
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                  <div className="p-5 rounded-xl bg-background-100 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-xs text-foreground-500 mt-1">contact@hanmanwu.com</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                    <p className="text-xs text-foreground-500 mt-1">韩漫屋HanManWu</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-900">响应时间</p>
                    <p className="text-xs text-foreground-500 mt-1">24小时内回复</p>
                  </div>
                </div>

                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formMessage}</p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default AboutPage;